import { useState, type FormEvent, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';

import { ApiError } from '../../shared/api/client.ts';
import {
  useArquivosDoPortal,
  useBaixarPlanilhaDoPortal,
  useBaixarXmlDoPortal,
  useBaixarZipDoPortal,
  usePortal,
  type FiltroArquivosFiscais,
} from '../../shared/api/queries.ts';
import {
  formatarData,
  formatarDataSimples,
  formatarMoedaDeCentavos,
} from '../../shared/format.ts';
import { Logo } from '../../shared/ui/Logo.tsx';

/**
 * A página que o contador abre pelo link do e-mail.
 *
 * É a única tela do sistema sem login, e por isso ela é deliberadamente
 * estreita: mostra as notas de **um** cliente, deixa escolher o período e
 * baixar. Não tem menu, não lista clientes, não edita nada. Quem diz de quem
 * são estes arquivos é o servidor, a partir do token da URL — a página não
 * manda `empresaId` em lugar nenhum, e é essa ausência que impede um link de
 * alcançar o cliente de outro escritório.
 *
 * O `.zip` daqui sai com os XMLs e a planilha de conferência juntos: aqui não
 * há um segundo botão para quem esquecer do primeiro.
 */
export function PortalDoContador() {
  const { token = '' } = useParams<{ token: string }>();
  const portal = usePortal(token);

  // Enquanto o portal não carrega, não existe período para pedir. Depois, o
  // padrão é o do e-mail que trouxe o link: o contador que só quer o mês
  // anunciado não precisa escolher nada.
  const sugerido = portal.data?.periodoSugerido ?? null;
  const [editado, setEditado] = useState<{ de: string; ate: string } | null>(null);
  const [aplicado, setAplicado] = useState<{ de: string; ate: string } | null>(null);

  const periodo = editado ?? sugerido;
  const filtro: FiltroArquivosFiscais | null = aplicado ?? sugerido;

  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(50);

  const lista = useArquivosDoPortal(
    token,
    filtro ?? {},
    { limite: porPagina, deslocamento: pagina * porPagina },
    filtro !== null,
  );

  const baixarZip = useBaixarZipDoPortal(token);
  const baixarPlanilha = useBaixarPlanilhaDoPortal(token);
  const baixarXml = useBaixarXmlDoPortal(token);

  const arquivos = lista.data?.itens ?? [];
  const resumo = lista.data?.resumo;
  const total = lista.data?.total ?? 0;

  function pesquisar(evento: FormEvent) {
    evento.preventDefault();
    if (periodo === null) return;
    setPagina(0);
    setAplicado(periodo);
  }

  if (portal.isPending) {
    return (
      <Moldura>
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={120} />
          <Skeleton variant="rounded" height={280} />
        </Stack>
      </Moldura>
    );
  }

  // Link vencido, revogado ou inventado chegam todos aqui, com a mesma frase:
  // distinguir os casos diria a quem está tentando adivinhar uma URL que há
  // algo do outro lado.
  if (portal.isError || portal.data === undefined) {
    return (
      <Moldura>
        <Alert severity="warning">
          <AlertTitle>Este link não está mais disponível</AlertTitle>
          <Typography variant="body2">
            {portal.error instanceof ApiError
              ? portal.error.message
              : 'Não foi possível abrir o link. Peça um link novo ao escritório de contabilidade.'}
          </Typography>
        </Alert>
      </Moldura>
    );
  }

  const cliente = portal.data.cliente;

  return (
    <Moldura>
      <Stack spacing={2}>
        <Card>
          <Toolbar sx={{ gap: 2, flexWrap: 'wrap', py: 2 }}>
            <Box sx={{ flexGrow: 1, minWidth: 220 }}>
              <Typography variant="h2">{cliente.razaoSocial}</Typography>
              <Typography variant="body2" color="text.secondary">
                CNPJ {cliente.cnpjFormatado}
                {cliente.nomeFantasia !== null && ` — ${cliente.nomeFantasia}`}
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<GridOnOutlinedIcon />}
              onClick={() => void baixarPlanilha.mutateAsync(filtro ?? {}).catch(() => undefined)}
              disabled={arquivos.length === 0}
              loading={baixarPlanilha.isPending}
            >
              Só a planilha
            </Button>

            <Button
              variant="contained"
              startIcon={<FolderZipOutlinedIcon />}
              onClick={() => void baixarZip.mutateAsync(filtro ?? {}).catch(() => undefined)}
              disabled={arquivos.length === 0}
              loading={baixarZip.isPending}
            >
              Baixar XMLs e planilha
            </Button>
          </Toolbar>

          <Divider />

          <CardContent component="form" onSubmit={pesquisar}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ alignItems: { sm: 'center' } }}
            >
              <TextField
                label="De"
                type="date"
                value={periodo?.de ?? ''}
                onChange={(evento) =>
                  setEditado({ de: evento.target.value, ate: periodo?.ate ?? '' })
                }
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                label="Até"
                type="date"
                value={periodo?.ate ?? ''}
                onChange={(evento) =>
                  setEditado({ de: periodo?.de ?? '', ate: evento.target.value })
                }
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <Button
                type="submit"
                variant="outlined"
                startIcon={<SearchOutlinedIcon />}
                loading={lista.isFetching}
              >
                Pesquisar
              </Button>

              <Box sx={{ flexGrow: 1 }} />

              <Typography variant="caption" color="text.secondary">
                Link válido até {formatarData(portal.data.expiraEm)}
              </Typography>
            </Stack>

            {periodo !== null && filtro !== null && mudou(filtro, periodo) && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1.5, display: 'block' }}
              >
                O período mudou. Clique em Pesquisar para atualizar a lista.
              </Typography>
            )}
          </CardContent>
        </Card>

        {baixarZip.isError && <Alert severity="error">{mensagemDoErro(baixarZip.error)}</Alert>}
        {baixarPlanilha.isError && (
          <Alert severity="error">{mensagemDoErro(baixarPlanilha.error)}</Alert>
        )}

        <Card>
          <CardContent>
            {lista.isError ? (
              <Alert severity="error">
                Não foi possível carregar as notas. {mensagemDoErro(lista.error)}
              </Alert>
            ) : lista.isPending ? (
              <Stack spacing={1}>
                {Array.from({ length: 5 }, (_, indice) => (
                  <Skeleton key={indice} variant="rounded" height={40} />
                ))}
              </Stack>
            ) : arquivos.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 6, textAlign: 'center' }}
              >
                Nenhuma nota neste período.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Documento</TableCell>
                      <TableCell>Chave de acesso</TableCell>
                      <TableCell align="right">Número</TableCell>
                      <TableCell align="right">Série</TableCell>
                      <TableCell>Emissão</TableCell>
                      <TableCell align="right">Valor</TableCell>
                      <TableCell align="right">XML</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {arquivos.map((arquivo) => (
                      <TableRow key={arquivo.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={arquivo.modelo === '65' ? 'NFC-e' : 'NF-e'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="caption"
                            sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}
                          >
                            {arquivo.chave}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {arquivo.numero}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {Number(arquivo.serie)}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatarData(arquivo.emitidoEm)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                        >
                          {formatarMoedaDeCentavos(arquivo.valorCentavos)}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Baixar o XML desta nota">
                            <IconButton
                              size="small"
                              onClick={() =>
                                void baixarXml.mutateAsync(arquivo.id).catch(() => undefined)
                              }
                              aria-label={`Baixar o XML da nota ${arquivo.numero}`}
                            >
                              <DownloadOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>

          {total > 0 && (
            <TablePagination
              component="div"
              count={total}
              page={pagina}
              onPageChange={(_, nova) => setPagina(nova)}
              rowsPerPage={porPagina}
              onRowsPerPageChange={(evento) => {
                setPorPagina(Number(evento.target.value));
                setPagina(0);
              }}
              rowsPerPageOptions={[25, 50, 100, 200]}
              labelRowsPerPage="Por página"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            />
          )}
        </Card>

        {resumo !== undefined && resumo.quantidade > 0 && filtro !== null && (
          <Stack
            direction="row"
            spacing={3}
            sx={{ flexWrap: 'wrap', px: 1, color: 'text.secondary' }}
          >
            <Typography variant="body2">
              <strong>{resumo.quantidade}</strong> nota(s) de{' '}
              {formatarDataSimples(filtro.de ?? '')} a {formatarDataSimples(filtro.ate ?? '')}
            </Typography>
            <Typography variant="body2">
              Entradas: <strong>{formatarMoedaDeCentavos(resumo.entradasCentavos)}</strong>
            </Typography>
            <Typography variant="body2">
              Saídas: <strong>{formatarMoedaDeCentavos(resumo.saidasCentavos)}</strong>
            </Typography>
          </Stack>
        )}
      </Stack>
    </Moldura>
  );
}

/**
 * O enquadramento da página pública.
 *
 * Sem barra lateral e sem menu de usuário: quem abre isto não tem conta no
 * sistema, e oferecer navegação seria oferecer portas fechadas.
 */
function Moldura({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: 'background.default' }}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          py: 1.5,
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            <Logo altura={36} />
            <Box>
              <Typography variant="subtitle2">Contábil RK Solutions</Typography>
              <Typography variant="caption" color="text.secondary">
                Arquivos fiscais para a contabilidade
              </Typography>
            </Box>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {children}
      </Container>
    </Box>
  );
}

function mudou(aplicado: { de?: string; ate?: string }, periodo: { de: string; ate: string }) {
  return aplicado.de !== periodo.de || aplicado.ate !== periodo.ate;
}

function mensagemDoErro(erro: unknown): string {
  if (erro instanceof ApiError) return erro.message;
  return erro instanceof Error ? erro.message : 'Falha inesperada.';
}
